import { tz } from '@date-fns/tz';
import { add, format, parseISO } from 'date-fns';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { STANDARD_DATE_TIME_FORMAT_DATE_FNS } from '@savgroup-front-common/constants';
import { getTechnicalInterventionsByFileId } from '@savgroup-front-common/core/domains/technicalInterventions/actionCreators';
import { selectTechnicalInterventionsByFileId } from '@savgroup-front-common/core/domains/technicalInterventions/selectors';
import { safeFormattedIntlString } from '@savgroup-front-common/core/formatters';
import { useGetSellerConfiguration } from '@savgroup-front-common/core/hooks';
import { useBanners } from '@savgroup-front-common/core/molecules/NotificationsProvider';
import {
  SALESFORCE_INTEGRATION_TYPE,
  TECHNICAL_INTERVENTION_STATUS,
} from '@savgroup-front-common/types';
import { WorkflowService } from '@/api';

import { useMyAccountTypedSelector } from '../../../../../../hooks';
import { useFileFollowupsContext } from '../../FileFollowups.context';

import { reformatDate } from './ConfirmedTechnicalInterventionSchedule.formatters';
import messages from './messages';

export const useConfirmedTechnicalInterventionSchedule = ({
  fileId,
}: {
  fileId: string;
}) => {
  const { fileSummary } = useFileFollowupsContext();

  const dispatch = useDispatch();
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isRescheduleModalOpen, setIsRescheduleModalOpen] = useState(false);
  const [
    isCancelTechnicalInterventionLoading,
    setIsCancelTechnicalInterventionLoading,
  ] = useState(false);

  const { pushErrors } = useBanners({
    extendedMessages: messages,
  });

  useEffect(() => {
    if (fileId) {
      dispatch(getTechnicalInterventionsByFileId({ fileId }));
    }
  }, [dispatch, fileId]);

  const latestTechnicalIntervention = useMyAccountTypedSelector((store) =>
    selectTechnicalInterventionsByFileId(store, { fileId }),
  );
  const handleCancelTechnicalInterventionSchedule = useCallback(async () => {
    setIsCancelTechnicalInterventionLoading(true);

    const response = await WorkflowService.cancelTechnicalIntervention({
      fileId,
    });

    if (response.failure) {
      setIsCancelTechnicalInterventionLoading(false);
      pushErrors(response.errors);
    }

    setIsConfirmModalOpen(false);
  }, [fileId, pushErrors]);

  const { sellerConfiguration } = useGetSellerConfiguration({
    sellerId: fileSummary?.sellerId,
  });

  if (
    fileSummary &&
    sellerConfiguration?.salesforceFieldServiceLightningIntegrationType ===
      SALESFORCE_INTEGRATION_TYPE.Integration
  ) {
    const fakeStartDate = add(new Date(), { days: 1 });
    const fakeEndDate = add(new Date(), { days: 1 });

    fakeStartDate.setHours(10);
    fakeStartDate.setMinutes(0);

    fakeEndDate.setHours(11);
    fakeEndDate.setMinutes(0);

    return {
      setIsRescheduleModalOpen,
      isConfirmModalOpen,
      isRescheduleModalOpen,
      confirmedScheduleText: safeFormattedIntlString(
        messages.technicalInterventionAppointmentValue,
        {
          startDate: format(fakeStartDate, 'dd/MM/yyyy'),
          startTime: format(fakeStartDate, 'HH:mm'),
          endTime: format(fakeEndDate, 'HH:mm'),
        },
      ),
      latestTechnicalIntervention: {
        startTimeUtc: format(fakeStartDate, 'HH:mm'),
        endTimeUtc: format(fakeEndDate, 'HH:mm'),
        status: TECHNICAL_INTERVENTION_STATUS.SCHEDULED,
        createdDate: '',
        isReSchedulable: true,
      },
      handleCancelTechnicalInterventionSchedule,
      isCancelTechnicalInterventionLoading,
      setIsConfirmModalOpen,
    };
  }

  if (!latestTechnicalIntervention) {
    return {
      setIsRescheduleModalOpen,
      isConfirmModalOpen,
      isRescheduleModalOpen,
      latestTechnicalIntervention,
      handleCancelTechnicalInterventionSchedule,
      isCancelTechnicalInterventionLoading,
      setIsConfirmModalOpen,
    };
  }

  const startDate = reformatDate({
    date:
      latestTechnicalIntervention.startTimeUtc &&
      format(parseISO(latestTechnicalIntervention.startTimeUtc), STANDARD_DATE_TIME_FORMAT_DATE_FNS, {
        in: tz('UTC'),
      }),
    integrationType:
      sellerConfiguration?.salesforceFieldServiceLightningIntegrationType,
    destinationFormat: 'dd/MM/yyyy',
  });
  const startTimeUtc = reformatDate({
    date:
      latestTechnicalIntervention.startTimeUtc &&
      format(parseISO(latestTechnicalIntervention.startTimeUtc), STANDARD_DATE_TIME_FORMAT_DATE_FNS, {
        in: tz('UTC'),
      }),
    integrationType:
      sellerConfiguration?.salesforceFieldServiceLightningIntegrationType,
    destinationFormat: 'HH:mm',
  });

  const endTimeUtc = reformatDate({
    date:
      latestTechnicalIntervention.endTimeUtc &&
      format(parseISO(latestTechnicalIntervention.endTimeUtc), STANDARD_DATE_TIME_FORMAT_DATE_FNS, {
        in: tz('UTC'),
      }),
    integrationType:
      sellerConfiguration?.salesforceFieldServiceLightningIntegrationType,
    destinationFormat: 'HH:mm',
  });

  const confirmedScheduleText = safeFormattedIntlString(
    messages.technicalInterventionAppointmentValue,
    {
      startDate,
      startTime: startTimeUtc,
      endTime: endTimeUtc,
    },
  );

  return {
    setIsRescheduleModalOpen,
    isConfirmModalOpen,
    isRescheduleModalOpen,
    confirmedScheduleText,
    latestTechnicalIntervention,
    handleCancelTechnicalInterventionSchedule,
    isCancelTechnicalInterventionLoading,
    setIsConfirmModalOpen,
  };
};
